/**
 * Apartsales — /search route.
 *
 * Two states:
 *   1. IDLE — Aviasales-grade hero (HeroForm + scenarios + forecast + saved)
 *   2. RESULTS — list / map / compare with compact ResultsHeader
 *
 * Transition: tap big CTA in HeroForm → results.
 *             tap summary bar in ResultsHeader → back to idle.
 *
 * Spec: docs/architecture/26-search-and-filters-deep-dive.md
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_FILTERS,
  type SearchFilters,
} from '~/lib/search';
import {
  resetFilters,
  summarizeFilters,
  useRecentSearches,
  useSavedAlerts,
  useTitleFromFilters,
} from '~/lib/useSearchFilters';
import { useLiveSearch } from '~/lib/useLiveSearch';
import { ApiCallError } from '~/lib/api';
import { CompareView } from '~/components/search/CompareView';
import { AffordabilityMap } from '~/components/search/AffordabilityMap';
import { FilterSheet } from '~/components/search/FilterSheet';
import { IdleScreen } from '~/components/search/IdleScreen';
import { MapView } from '~/components/search/MapView';
import { ResultsHeader } from '~/components/search/ResultsHeader';
import { ResultsList } from '~/components/search/ResultsList';
import { SaveSearchSheet } from '~/components/search/SaveSearchSheet';
import { SearchShareSheet } from '~/components/search/SearchShareSheet';
import type { ResultsView } from '~/components/search/SearchHeader';

export const Route = createFileRoute('/search')({
  component: SearchScreen,
});

function SearchScreen() {
  // Handoff bootstrap happens exactly once — read sessionStorage, derive
  // initial filters + auto-submit flag together, then clear the key. Doing
  // this in a single shot avoids the bug where two separate useState
  // initializers race over the same sessionStorage key (the first consumes
  // it, the second sees null and returns false).
  const bootstrap = useMemo(() => readInitialState(), []);
  const [filters, setFilters] = useState<SearchFilters>(bootstrap.filters);
  const [submitted, setSubmitted] = useState(bootstrap.shouldSubmit);
  const [view, setView] = useState<ResultsView>('list');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [shareSearchOpen, setShareSearchOpen] = useState(false);
  const [affordabilityOpen, setAffordabilityOpen] = useState(false);

  // URL sync (?q=)
  useEffect(() => {
    writeQueryToUrl(filters.q);
  }, [filters.q]);

  // Live API search. Disabled in idle state so we don't burn requests on
  // every keystroke in the hero form — fires only after user submits.
  const live = useLiveSearch(filters, submitted);
  const results = live.items;
  useTitleFromFilters(filters, results.length);

  // Human-readable error message — short, actionable, no stack traces.
  const errorMessage =
    live.error instanceof ApiCallError
      ? live.error.status === 0 || live.error.code === 'network_error'
        ? 'Нет связи с сервером. Проверь интернет.'
        : live.error.message
      : live.error
        ? 'Что-то пошло не так. Повтори попытку через минуту.'
        : null;

  // Recent searches — debounced commit when user stops typing in q
  const { recent, add: addRecent, remove: removeRecent } = useRecentSearches();
  useEffect(() => {
    if (!filters.q.trim()) return;
    const id = window.setTimeout(() => {
      addRecent({ q: filters.q, summary: summarizeFilters(filters) });
    }, 1500);
    return () => window.clearTimeout(id);
  }, [filters.q, filters, addRecent]);

  // Saved alerts
  const { saved, add: addSaved, remove: removeSaved, toggle: toggleSaved } = useSavedAlerts();

  return (
    <section className="relative min-h-full px-4 pb-8">
      {!submitted ? (
        <IdleScreen
          filters={filters}
          onChange={setFilters}
          onSubmit={() => setSubmitted(true)}
          onOpenAdvanced={() => setFiltersOpen(true)}
          onOpenAffordabilityMap={() => setAffordabilityOpen(true)}
          recent={recent}
          removeRecent={removeRecent}
          saved={saved}
          toggleSaved={toggleSaved}
          removeSaved={removeSaved}
          applySaved={(f) => {
            setFilters(f);
            setSubmitted(true);
          }}
        />
      ) : (
        <>
          <ResultsHeader
            filters={filters}
            resultCount={results.length}
            view={view}
            onViewChange={setView}
            onEditSearch={() => setSubmitted(false)}
            onOpenFilters={() => setFiltersOpen(true)}
            onShare={() => setShareSearchOpen(true)}
          />
          {view === 'list' ? (
            <ResultsList
              filters={filters}
              onChange={setFilters}
              results={results}
              onSaveSearch={() => setSaveOpen(true)}
              isLoading={live.isLoading}
              isError={live.isError}
              errorMessage={errorMessage}
              onRetry={live.refetch}
            />
          ) : view === 'map' ? (
            <MapView results={results} />
          ) : (
            <CompareView results={results} />
          )}
        </>
      )}

      {/* Modals */}
      <FilterSheet
        open={filtersOpen}
        filters={filters}
        onChange={setFilters}
        onClose={() => setFiltersOpen(false)}
        onReset={() => setFilters(resetFilters(filters))}
      />

      <SaveSearchSheet
        open={saveOpen}
        defaultName={summarizeFilters(filters) || 'Мой поиск'}
        onSave={(name, alertOn) => addSaved({ name, filters, alertOn })}
        onClose={() => setSaveOpen(false)}
      />

      <AffordabilityMap
        open={affordabilityOpen}
        budget={filters.priceMax === DEFAULT_FILTERS.priceMax ? 10_000_000 : filters.priceMax}
        onBudgetChange={(priceMax) => setFilters({ ...filters, priceMax })}
        onSelectCity={(city) => {
          setFilters({ ...filters, cities: [city] });
          setSubmitted(true);
        }}
        onClose={() => setAffordabilityOpen(false)}
      />

      <SearchShareSheet
        open={shareSearchOpen}
        filters={filters}
        results={results}
        onClose={() => setShareSearchOpen(false)}
      />
    </section>
  );
}

/* ────────────────────────────────────────────────────────────── */

/**
 * Reads initial filters AND auto-submit intent in one go:
 *   1. sessionStorage 'apartsales:search-handoff' — set by home pills/HeroForm
 *      when user taps "Найти" from main page. Presence implies submit-intent.
 *      Consumed exactly once.
 *   2. URL `?q=` query param — direct linking; only set filters, don't submit
 *      (user might still be typing).
 *   3. DEFAULT_FILTERS otherwise — pure idle entry.
 *
 * Combined into one function so we read sessionStorage exactly once. Two
 * separate useState initializers racing over the same key would lose the
 * handoff signal — the first consumer clears it, the second sees null.
 */
interface InitialState {
  filters: SearchFilters;
  shouldSubmit: boolean;
}

function readInitialState(): InitialState {
  if (typeof window === 'undefined') {
    return { filters: DEFAULT_FILTERS, shouldSubmit: false };
  }

  // Handoff from home page — both filters + submit intent
  const handoff = window.sessionStorage.getItem('apartsales:search-handoff');
  if (handoff) {
    window.sessionStorage.removeItem('apartsales:search-handoff');
    try {
      const parsed = JSON.parse(handoff);
      return {
        filters: { ...DEFAULT_FILTERS, ...parsed },
        shouldSubmit: true,
      };
    } catch {
      // Fall through to URL handling
    }
  }

  // URL ?q= param — preserves search across reload; doesn't auto-submit
  const sp = new URLSearchParams(window.location.search);
  const q = sp.get('q') ?? '';
  return {
    filters: { ...DEFAULT_FILTERS, q },
    shouldSubmit: false,
  };
}

function writeQueryToUrl(q: string) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (q.trim()) url.searchParams.set('q', q);
  else url.searchParams.delete('q');
  window.history.replaceState(null, '', url.toString());
}
